'use client';

import React from 'react';
import {
  AuthorDocument,
  BlogDocument,
  WebinarPostDocument,
} from '../../../prismicio-types';
import { asText, RTHeading2Node } from '@prismicio/client';
import { JSXMapSerializer, PrismicRichText } from '@prismicio/react';
import { ArticleImageRibbon } from './ArticleImageRibbon';
import { BlogHero } from './BlogHero';
import { BlogMetaBar } from './BlogMetaBar';
import { BlogToc, BlogTocMobile, TocSection } from './BlogToc';
import { BlogVideo } from './BlogVideo';
import { getBlogPublishedDate } from '@/utils';

// Both types share the same content model (see customtypes/blog/index.json and
// customtypes/webinar_post/index.json). The union keeps a single renderer for
// article-shaped pages; the only per-type branch is the hero kicker.
export type Article = BlogDocument<string> | WebinarPostDocument<string>;

interface Props {
  article: Article;
  author: Partial<AuthorDocument>;
  imageLayout?: 'cover' | 'fill';
}

const slugify = (text: string) => {
  if (!text) {
    return '';
  }
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const kickerForArticle = (article: Article): string =>
  article.type === 'webinar_post' ? 'WEBINAR' : 'BLOG';

export const ArticleView = ({
  article,
  author,
  imageLayout = 'cover',
}: Props) => {
  // TOC sections: one entry per heading2, plus a synthetic "Introduction" ONLY
  // when the article opens with body content before its first heading.
  const tocSections: TocSection[] = React.useMemo(() => {
    const content = article.data.content;
    const headings = content
      .filter((block) => block.type === 'heading2')
      .map((block) => {
        const label = asText([block as RTHeading2Node]);
        return { id: slugify(label), label };
      });
    const startsWithTitle = content[0]?.type?.startsWith('heading') ?? false;
    return startsWithTitle
      ? headings
      : [{ id: 'introduction', label: 'Introduction' }, ...headings];
  }, [article.data.content]);

  const components: JSXMapSerializer = {
    heading2: ({ children, node }) => {
      const headingText = asText([node]);
      const id = slugify(headingText);
      return (
        <h2
          id={id}
          className="scroll-mt-[220px] lg:scroll-mt-24 font-inter font-bold text-[24px] leading-[1.4] text-[#041118]"
        >
          {children}
        </h2>
      );
    },
    heading3: ({ children, node }) => {
      const headingText = asText([node]);
      const id = slugify(headingText);
      return (
        <h3
          id={id}
          className="scroll-mt-[220px] lg:scroll-mt-24 font-inter font-bold text-[20px] leading-[1.4] text-[#041118]"
        >
          {children}
        </h3>
      );
    },
    heading4: ({ children, node }) => {
      const headingText = asText([node]);
      const id = slugify(headingText);
      return (
        <h4
          id={id}
          className="scroll-mt-[220px] lg:scroll-mt-24 font-inter font-semibold text-[18px] leading-[1.4] text-[#041118]"
        >
          {children}
        </h4>
      );
    },
    heading5: ({ children, node }) => {
      const headingText = asText([node]);
      const id = slugify(headingText);
      return (
        <h5
          id={id}
          className="scroll-mt-[220px] lg:scroll-mt-24 font-inter font-semibold text-[16px] leading-[1.4] text-[#041118]"
        >
          {children}
        </h5>
      );
    },
    heading6: ({ children, node }) => {
      const headingText = asText([node]);
      const id = slugify(headingText);
      return (
        <h6
          id={id}
          className="scroll-mt-[220px] lg:scroll-mt-24 font-inter font-semibold text-[14px] leading-[1.4] text-[#041118]"
        >
          {children}
        </h6>
      );
    },
    paragraph: ({ children }) => (
      <p className="font-inter font-normal text-[18px] leading-[1.5] text-[#041118]">
        {children}
      </p>
    ),
    strong: ({ children }) => (
      <strong className="font-inter font-bold text-inherit leading-inherit text-[#041118]">
        {children}
      </strong>
    ),
    em: ({ children }) => (
      <em className="font-inter font-normal italic text-inherit leading-inherit text-[#041118]">
        {children}
      </em>
    ),
  };

  return (
    <div className="bg-neutral-50 w-full">
      <BlogHero
        image={article.data.image}
        title={article.data.card_title}
        kicker={kickerForArticle(article)}
        imageLayout={imageLayout}
      />

      <div className="w-full bg-white px-5 pt-10 md:px-10 md:pt-[70px] lg:px-20">
        <div className="mx-auto w-full max-w-[1280px]">
          <BlogMetaBar
            authorName={author.data?.name || 'Devsu'}
            authorAvatar={author.data?.avatar}
            date={
              getBlogPublishedDate(article) || article.last_publication_date
            }
            readDuration={article.data.read_duration}
          />
        </div>
      </div>

      <BlogVideo
        embed={article.data.video_embed}
        file={article.data.video_file}
        url={article.data.video_url}
      />

      <ArticleImageRibbon items={article.data.media_items} />

      <div className="bg-white pt-16 pb-[70px] flex justify-center w-full px-5 md:px-10 lg:px-20">
        <div className="grid w-full max-w-[1280px] grid-cols-1 gap-10 lg:grid-cols-[282px_minmax(0,1fr)] lg:gap-x-[55px] lg:gap-y-10">
          <div className="order-first w-full lg:hidden sticky top-[156px] z-30">
            <BlogTocMobile sections={tocSections} />
          </div>

          <div className="hidden lg:block lg:col-start-1 lg:row-start-1 lg:self-start lg:sticky lg:top-[120px]">
            <BlogToc sections={tocSections} />
          </div>

          <div className="order-1 w-full lg:order-none lg:col-start-2 lg:row-start-1">
            <div
              id="blog-content-container"
              className="scroll-mt-[220px] lg:scroll-mt-24 [&_h2:not(:first-child)]:mt-9 [&_h3:not(:first-child)]:mt-6 [&_h4:not(:first-child)]:mt-6 [&_h5:not(:first-child)]:mt-5 [&_h6:not(:first-child)]:mt-4 [&_p:not(:first-child)]:mt-6 [&_ul:not(:first-child)]:mt-6 [&_ol:not(:first-child)]:mt-6 [&_li]:mt-2"
            >
              <PrismicRichText
                field={article.data.content}
                components={components}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
